import path from "path-browserify"
import { generatePath } from "react-router-dom"
import { encodeQueryParams } from "../utils/useControlledQueryParams"

export type CoverPageResourcePathRef = {
  id: number
  slug: string
}

/** How a cover-page route identifies the resource in the URL. */
export type CoverPageResourceKey = "id" | "slug"

export type CoverPageBrowseFilterParams = {
  category?: string
  author?: string
}

/**
 * Parent route for cover pages in this context, with `:resourceSlug` (and
 * anything after it — cohort, `/cover/`) stripped from the route pattern.
 *
 * Store: `/library/store/:schoolId/:resourceSlug/` → `/library/store/99/`
 * Library: `/library/:resourceSlug/:cohortId?/cover/` → `/library/`
 */
export function getCoversBaseRoute(
  coverPagePath: string,
  params: Record<string, string | number | boolean | undefined> = {}
): string {
  const resourceParamAt = coverPagePath.indexOf(":resourceSlug")
  const parentPattern =
    resourceParamAt >= 0
      ? coverPagePath.slice(0, resourceParamAt)
      : coverPagePath

  return path.join(generatePath(parentPattern, params), "/")
}

/**
 * Browse URL for a category/author filter from a cover page.
 * Uses the current cover base (library or store). Root covers (`/`) fall back
 * to `/library/` so pills do not navigate to `/?category=`.
 */
export function getCoverPageBrowseFilterPath(
  coverBaseRoute: string | undefined | null,
  filters: CoverPageBrowseFilterParams = {}
): string {
  const base =
    !coverBaseRoute || coverBaseRoute === "/" ? "/library/" : coverBaseRoute

  const paramsObj: Record<string, string[]> = {}
  if (filters.category) paramsObj.category = [filters.category]
  if (filters.author) paramsObj.author = [filters.author]

  return `${base}${encodeQueryParams(paramsObj)}`
}

/**
 * URL for another resource's cover page under `coverBaseRoute`.
 * Store routes identify by id; library routes always use `{slug}/cover/`.
 */
export function getCoverPageResourcePath(
  resource: CoverPageResourcePathRef,
  coverBaseRoute: string,
  resourceKey: CoverPageResourceKey = "slug"
): string {
  return resourceKey === "id"
    ? path.join(coverBaseRoute, String(resource.id), "/")
    : path.join(coverBaseRoute, resource.slug, "cover", "/")
}
